import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useLocale, SITE_ORIGIN } from "./useLocale";
import { SupportedLanguage } from "./config";
import seoRoutes from "./seoRoutes.json";

type SeoKey = keyof typeof seoRoutes.canonicalSubPath;

/** Route -> seo.* copy, canonical target, and og:locale all live in
 *  seoRoutes.json so that this hook (client-side, after hydration) and
 *  scripts/prerender-meta.js (build-time, writes the static per-route
 *  index.html files that non-JS crawlers see) can never drift apart.
 *
 *  Summary of the rules encoded there:
 *  - The scroll-anchor routes (about-us/, services/, ...) render the same
 *    Dashboard as "/", so they share its title/description and canonicalize
 *    back to "/" (or "/de/") instead of self-canonicalizing — consistent
 *    with sitemap.xml treating them as duplicates of the homepage.
 *  - privacyPolicy is intentionally NOT split into an "/en/" and "/de/"
 *    canonical pair: its bilingual legal text is handled by the page's own
 *    US/DE selector, not the site's routing, and sitemap.xml lists it as a
 *    single URL with no hreflang alternates. */
const ROUTE_SEO_KEY = seoRoutes.routeSeoKey as Record<string, SeoKey>;
const CANONICAL_SUBPATH = seoRoutes.canonicalSubPath as Record<SeoKey, string>;
const LOCALIZABLE_CANONICAL = seoRoutes.localizableCanonical as Record<
  SeoKey,
  boolean
>;
const OG_IMAGE = seoRoutes.ogImage as Record<SeoKey, string>;
const OG_LOCALE = seoRoutes.ogLocale as Record<SupportedLanguage, string>;

const setMetaContent = (selector: string, value: string) => {
  const el = document.head.querySelector<HTMLMetaElement>(selector);
  if (el) el.setAttribute("content", value);
};

const setOrCreateCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/** Keep <title>, meta description, Open Graph / Twitter Card text, and the
 *  canonical link in sync with the current route + language. This is a
 *  client-only SPA, so — same pattern as useHreflangTags — these are
 *  managed imperatively instead of via a <head> library.
 *
 *  og:image / twitter:image come from seoRoutes.json `ogImage` (the /tools/
 *  page has its own Write Better card). og:type / og:site_name are left as
 *  the static defaults already in index.html. */
export const useSeoTags = () => {
  const { t } = useTranslation();
  const { language, subPath } = useLocale();

  useEffect(() => {
    const key = ROUTE_SEO_KEY[subPath] ?? "home";
    const title = t(`seo.${key}.title`);
    const description = t(`seo.${key}.description`);

    // Canonicalize to the one indexable URL for this key (sitemap.xml),
    // not to whatever alias/scroll-anchor path is currently in the address
    // bar — this is what folds "/en/", "/about-us/" etc. back to the URL
    // Google should actually index.
    const canonicalSubPath = CANONICAL_SUBPATH[key];
    const canonicalPath =
      language === "de" && LOCALIZABLE_CANONICAL[key]
        ? `/de${canonicalSubPath}`
        : canonicalSubPath;
    const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;

    document.title = title;

    setMetaContent('meta[name="description"]', description);
    setMetaContent('meta[property="og:title"]', title);
    setMetaContent('meta[property="og:description"]', description);
    setMetaContent('meta[property="og:url"]', canonicalUrl);
    setMetaContent('meta[property="og:locale"]', OG_LOCALE[language]);
    setMetaContent('meta[property="og:image"]', `${SITE_ORIGIN}${OG_IMAGE[key]}`);
    setMetaContent('meta[name="twitter:image"]', `${SITE_ORIGIN}${OG_IMAGE[key]}`);
    setMetaContent('meta[name="twitter:title"]', title);
    setMetaContent('meta[name="twitter:description"]', description);

    setOrCreateCanonical(canonicalUrl);
  }, [language, subPath, t]);
};
